import { Alert, Select, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { filterOptionForSearch } from '../../helpers/normalizeForSearch';
import { MatchVm } from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages } from './insightsAccess';
import { useInsightsClub } from './insightsClubScope';
import {
    isFinishedMatch,
    MatchResult,
    ourTeamIds,
    perGame,
    summarizeClubMatches,
} from './insightsClubSummary';
import { buildGameStateSplits, mergeGameStateSplits, shootPct } from './insightsGameState';
import InsightsLegend from './InsightsLegend';
import {
    collectBundleFilterOptions,
    emptyMatchBundleFilters,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import InsightsMatchMaps from './InsightsMatchMaps';
import InsightsMatchTable from './InsightsMatchTable';
import InsightsNav from './InsightsNav';
import { fetchClubMatches, loadMatchBundles, MatchBundle } from './insightsQueries';
import InsightsScopeFilters from './InsightsScopeFilters';
import {
    ATTACK_LABELS,
    ATTACK_ORDER,
    AttackType,
    buildAttackSplits,
    buildPeriodSplits,
    buildQuarterControl,
    buildTeamScoreForm,
    buildTurnoverTypeRows,
    buildUsVsThem,
    buildVenueEventRows,
    buildVenueSplits,
    buildWhyWeWin,
    conversionPercent,
    gkSitPct,
    pctText,
    PeriodSplit,
    pkPct,
    savePct,
    shootingPct,
    tallyBundles,
    wdlText,
} from './insightsTeamSplits';
import InsightsTopbar from './InsightsTopbar';
import {
    makeRateCell,
    TwExclusionsBoard,
    TwSavesBoard,
    TwScorersBoard,
    TwSupportBoard,
} from './InsightsTwBoards';
import { buildTwPlayerBoards } from './insightsTwPlayerTables';

interface Props extends RouteComponentProps {
    userProfile?: Profile;
}

const PAGE_SIZE = 100;

interface OpponentOption {
    teamId: number;
    name: string;
    meetings: number;
}

/** Opponents we actually played, taken from our own match list. */
const opponentsFromMatches = (matches: MatchVm[], teamIds: number[]): OpponentOption[] => {
    const byTeamId = new Map<number, OpponentOption>();

    matches.forEach((match: MatchVm) => {
        const weWereHome = teamIds.includes(match.homeTeamId);
        const weWereAway = teamIds.includes(match.awayTeamId);
        if (!weWereHome && !weWereAway) return;

        const teamId = weWereHome ? match.awayTeamId : match.homeTeamId;
        const name =
            (weWereHome ? match.awayTeamDisplayName : match.homeTeamDisplayName) || 'Opponent';
        if (teamId == null || teamIds.includes(teamId)) return;

        const existing = byTeamId.get(teamId);
        if (existing) existing.meetings += 1;
        else byTeamId.set(teamId, { teamId, name, meetings: 1 });
    });

    return Array.from(byTeamId.values()).sort(
        (a, b) => b.meetings - a.meetings || a.name.localeCompare(b.name)
    );
};

const formPips = (results: MatchResult[]): React.ReactNode => {
    if (results.length === 0) return <b>-</b>;
    return (
        <div className="insightsFormRow">
            {results.map((result, index) => (
                <span
                    key={`${result}-${index}`}
                    className={`insightsFormPip is-${result.toLowerCase()}`}
                >
                    {result}
                </span>
            ))}
        </div>
    );
};

const percentText = (goals: number, shots: number): string => {
    const value = shots > 0 ? Math.round((goals / shots) * 100) : undefined;
    return pctText(value);
};

const parseOpponentQuery = (search: string): number | undefined => {
    const raw = new URLSearchParams(search).get('opponentTeamId');
    if (raw == null || raw === '') return undefined;
    const id = parseInt(raw, 10);
    return Number.isFinite(id) ? id : undefined;
};

const InsightsPrep: React.FC<Props> = ({ userProfile, location, history }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [clubMatches, setClubMatches] = useState<MatchVm[]>([]);
    const [opponentTeamId, setOpponentTeamId] = useState<number | undefined>(() =>
        parseOpponentQuery(location.search)
    );
    const [allBundles, setAllBundles] = useState<MatchBundle[]>([]);
    const [h2hMatches, setH2hMatches] = useState<MatchVm[]>([]);
    const [filters, setFilters] = useState<MatchBundleFilterSelection>(emptyMatchBundleFilters());
    const [loadingClub, setLoadingClub] = useState(false);
    const [loadingH2h, setLoadingH2h] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (!allowed || !canUseInsightsPages(userRole) || selectedClubId == null) {
                setClubMatches([]);
                return;
            }
            setLoadingClub(true);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;
                setClubMatches(page.pageItems || []);
            } catch {
                if (!cancelled) setClubMatches([]);
            } finally {
                if (!cancelled) setLoadingClub(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId]);

    const opponents = useMemo(
        () => opponentsFromMatches(clubMatches, teamIds),
        [clubMatches, teamIds]
    );

    useEffect(() => {
        setOpponentTeamId((current) => {
            const fromQuery = parseOpponentQuery(location.search);
            if (fromQuery != null && opponents.some((o) => o.teamId === fromQuery)) {
                return fromQuery;
            }
            if (current != null && opponents.some((o) => o.teamId === current)) return current;
            return opponents[0]?.teamId;
        });
    }, [opponents, location.search]);

    const selectOpponent = (value: number): void => {
        setOpponentTeamId(value);
        const params = new URLSearchParams(location.search);
        params.set('opponentTeamId', String(value));
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    };

    useEffect(() => {
        let cancelled = false;

        const loadH2h = async (): Promise<void> => {
            if (!allowed || opponentTeamId == null) {
                setAllBundles([]);
                setH2hMatches([]);
                return;
            }

            const meetings = clubMatches.filter(
                (match: MatchVm) =>
                    isFinishedMatch(match) &&
                    (match.homeTeamId === opponentTeamId || match.awayTeamId === opponentTeamId)
            );

            if (meetings.length === 0) {
                setAllBundles([]);
                setH2hMatches([]);
                return;
            }

            setLoadingH2h(true);
            setError(undefined);
            try {
                // All finished meetings vs this opponent in the loaded club pool (no last-5 cap).
                const bundles = await loadMatchBundles(meetings);
                if (cancelled) return;
                setH2hMatches(meetings);
                setAllBundles(bundles);
                setFilters(emptyMatchBundleFilters());
            } catch {
                if (!cancelled) {
                    setAllBundles([]);
                    setH2hMatches([]);
                    setError('Could not load head to head numbers.');
                }
            } finally {
                if (!cancelled) setLoadingH2h(false);
            }
        };

        loadH2h();
        return () => {
            cancelled = true;
        };
    }, [allowed, clubMatches, opponentTeamId]);

    const filterOptions = useMemo(
        () => collectBundleFilterOptions(allBundles, teamIds),
        [allBundles, teamIds]
    );
    const filteredBundles = useMemo(
        () => filterMatchBundles(allBundles, filters),
        [allBundles, filters]
    );
    const filteredMatches = useMemo(() => {
        const ids = new Set(filteredBundles.map((b) => b.detail.id));
        return h2hMatches.filter((m) => ids.has(m.id));
    }, [h2hMatches, filteredBundles]);

    const opponentName = opponents.find((o) => o.teamId === opponentTeamId)?.name;
    const usTally = useMemo(
        () => tallyBundles(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const themTally = useMemo(
        () =>
            opponentTeamId != null ? tallyBundles(filteredBundles, [opponentTeamId]) : undefined,
        [filteredBundles, opponentTeamId]
    );
    const usVsThem = useMemo(
        () =>
            opponentTeamId != null ? buildUsVsThem(filteredBundles, teamIds, opponentTeamId) : [],
        [filteredBundles, teamIds, opponentTeamId]
    );
    const attack = useMemo(
        () =>
            filteredBundles.length > 0 ? buildAttackSplits(filteredBundles, teamIds) : undefined,
        [filteredBundles, teamIds]
    );
    const scoreForm = useMemo(
        () => buildTeamScoreForm(filteredMatches, teamIds),
        [filteredMatches, teamIds]
    );
    const record = useMemo(
        () => summarizeClubMatches(filteredMatches, teamIds, 10),
        [filteredMatches, teamIds]
    );
    const periods = useMemo(
        () => buildPeriodSplits(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const quarterControl = useMemo(
        () => buildQuarterControl(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const venue = useMemo(
        () => (filteredMatches.length > 0 ? buildVenueSplits(filteredMatches, teamIds) : undefined),
        [filteredMatches, teamIds]
    );
    const venueEvents = useMemo(
        () => buildVenueEventRows(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const whyWeWin = useMemo(
        () => buildWhyWeWin(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const turnoverRows = useMemo(() => buildTurnoverTypeRows(usTally), [usTally]);
    const gameState = useMemo(() => {
        if (filteredBundles.length === 0) return undefined;
        return mergeGameStateSplits(
            filteredBundles.map((bundle) => buildGameStateSplits(bundle, teamIds))
        );
    }, [filteredBundles, teamIds]);

    const ourBoards = useMemo(
        () => buildTwPlayerBoards(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const oppBoards = useMemo(
        () =>
            opponentTeamId != null ? buildTwPlayerBoards(filteredBundles, [opponentTeamId]) : [],
        [filteredBundles, opponentTeamId]
    );
    const rateCell = useMemo(() => makeRateCell('totals'), []);

    const ourScorers = useMemo(
        () => ourBoards.filter((r) => r.shots.total.attempts > 0 || r.shots.total.goals > 0),
        [ourBoards]
    );
    const ourKeepers = useMemo(
        () => ourBoards.filter((r) => r.saves.total.attempts > 0),
        [ourBoards]
    );
    const ourSupport = useMemo(
        () =>
            ourBoards.filter(
                (r) => r.assists > 0 || r.blocks > 0 || r.steals > 0 || r.turnovers > 0
            ),
        [ourBoards]
    );
    const ourExclusions = useMemo(
        () => ourBoards.filter((r) => r.exclusionsDrawn > 0 || r.exclusionsCommitted > 0),
        [ourBoards]
    );
    const oppScorers = useMemo(
        () => oppBoards.filter((r) => r.shots.total.attempts > 0 || r.shots.total.goals > 0),
        [oppBoards]
    );
    const oppKeepers = useMemo(
        () => oppBoards.filter((r) => r.saves.total.attempts > 0),
        [oppBoards]
    );
    const showOppBoards = oppScorers.length > 0 || oppKeepers.length > 0;

    const showGameState =
        gameState &&
        (gameState.leading.shots > 0 ||
            gameState.tied.shots > 0 ||
            gameState.trailing.shots > 0 ||
            gameState.clutch.shots > 0);

    const penaltyKill = attack ? conversionPercent(attack.them.extraPlayer) : undefined;
    const loading = loadingClub || loadingH2h || scope.loading;

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope} />

            <div className="insightsBody">
                <InsightsNav active="prep" />
                <InsightsLegend />

                <div className="insightsHero">
                    <div>
                        <h1>H2H (Match preparation)</h1>
                        <p>
                            How <strong>{opponentName || 'this opponent'}</strong> played{' '}
                            <strong>against you</strong>. Team patterns only - we do not track
                            opposing squads.
                        </p>
                    </div>
                    <div className="insightsFilterField insightsSampleField">
                        <label htmlFor="insights-prep-opponent">Opponent</label>
                        <Select
                            id="insights-prep-opponent"
                            showSearch
                            filterOption={filterOptionForSearch}
                            optionFilterProp="label"
                            placeholder="Select opponent"
                            value={opponentTeamId}
                            onChange={selectOpponent}
                            options={opponents.map((option: OpponentOption) => ({
                                value: option.teamId,
                                label: `${option.name} (${option.meetings})`,
                            }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                {allBundles.length > 0 ? (
                    <InsightsScopeFilters
                        options={filterOptions}
                        filters={filters}
                        onChange={setFilters}
                        matchCount={filteredBundles.length}
                    />
                ) : null}

                {error && (
                    <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                )}

                {opponents.length === 0 && !scope.loading && (
                    <Alert
                        type="info"
                        showIcon
                        message="No opponents yet"
                        description="Play and log a match to build a head to head profile."
                        style={{ marginBottom: 14 }}
                    />
                )}

                <Spin spinning={loading}>
                    {filteredMatches.length > 0 ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Briefing</h2>
                                <span>Vs {opponentName || 'opponent'} · filtered meetings</span>
                            </div>
                            <div className="insightsStatStrip">
                                <div className="insightsStat">
                                    {formPips(scoreForm.last10)}
                                    <i>Last 10</i>
                                </div>
                                <div className="insightsStat">
                                    <b>
                                        {record.wins}-{record.draws}-{record.losses}
                                    </b>
                                    <i>W-D-L</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{perGame(record.goalsFor, record.played)}</b>
                                    <i>GF / g</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{perGame(record.goalsAgainst, record.played)}</b>
                                    <i>GA / g</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{pctText(shootingPct(usTally))}</b>
                                    <i>Shooting</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{pctText(conversionPercent(usTally.attack.extraPlayer))}</b>
                                    <i>EP %</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{pctText(pkPct(usTally))}</b>
                                    <i>PK %</i>
                                </div>
                                <div className="insightsStat">
                                    <b>
                                        {usTally.swimoffs === 0
                                            ? '-'
                                            : `${usTally.swimoffWins}/${usTally.swimoffs}`}
                                    </b>
                                    <i>Swim-off</i>
                                </div>
                            </div>
                        </section>
                    ) : null}

                    {usVsThem.length > 0 || attack ? (
                        <>
                            <section className="insightsPanel">
                                <div className="insightsPanelHead">
                                    <h2>Us vs them</h2>
                                    <span>
                                        {filteredBundles.length > 0
                                            ? `${filteredBundles.length} meetings`
                                            : 'No finished meetings'}
                                    </span>
                                </div>
                                {usVsThem.length === 0 ? (
                                    <div className="insightsEmpty">
                                        No finished match against this opponent yet.
                                    </div>
                                ) : (
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Us</th>
                                                <th>{opponentName || 'Them'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usVsThem.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.left}</td>
                                                    <td>{row.right}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </section>

                            {attack ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Special teams and attack types</h2>
                                        <span>
                                            {penaltyKill == null
                                                ? 'Penalty kill needs opponent extra-player shots'
                                                : `Our PK ${100 - penaltyKill}%`}
                                        </span>
                                    </div>
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Attack</th>
                                                    <th>Our goals</th>
                                                    <th>Our shots</th>
                                                    <th>Ours</th>
                                                    <th>Their goals</th>
                                                    <th>Their shots</th>
                                                    <th>Theirs</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ATTACK_ORDER.map((type: AttackType) => (
                                                    <tr key={type}>
                                                        <td>{ATTACK_LABELS[type]}</td>
                                                        <td>{attack.us[type].goals}</td>
                                                        <td>{attack.us[type].shots}</td>
                                                        <td>
                                                            {pctText(
                                                                conversionPercent(attack.us[type])
                                                            )}
                                                        </td>
                                                        <td>{attack.them[type].goals}</td>
                                                        <td>{attack.them[type].shots}</td>
                                                        <td>
                                                            {pctText(
                                                                conversionPercent(attack.them[type])
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="insightsTableNote">
                                        Their columns = {opponentName || 'opponent'} against us in
                                        these meetings, not their season.
                                    </p>
                                </section>
                            ) : null}
                        </>
                    ) : null}

                    {filteredBundles.length > 0 ? (
                        <InsightsMatchMaps
                            bundles={filteredBundles}
                            ourTeamIds={teamIds}
                            contextLabel="these meetings"
                            opponentName={opponentName}
                        />
                    ) : null}

                    {periods.length > 0 || quarterControl.length > 0 ? (
                        <div className="insightsTableCluster">
                            {periods.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>By period</h2>
                                        <span>Our totals vs {opponentName || 'them'}</span>
                                    </div>
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Period</th>
                                                    <th>GF:GA</th>
                                                    <th>Shots</th>
                                                    <th>%</th>
                                                    <th>TO</th>
                                                    <th>ExC</th>
                                                    <th>ExD</th>
                                                    <th>EP</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {periods.map((split: PeriodSplit) => (
                                                    <tr key={split.periodId}>
                                                        <td>{split.period}</td>
                                                        <td>
                                                            {split.goalsFor}:{split.goalsAgainst}
                                                        </td>
                                                        <td>
                                                            {split.shotsFor}:{split.shotsAgainst}
                                                        </td>
                                                        <td>
                                                            {percentText(
                                                                split.goalsFor,
                                                                split.shotsFor
                                                            )}
                                                        </td>
                                                        <td>{split.turnoversFor}</td>
                                                        <td>{split.exclusionsCommitted}</td>
                                                        <td>{split.exclusionsDrawn}</td>
                                                        <td>
                                                            {split.extraPlayerShots === 0
                                                                ? '-'
                                                                : `${split.extraPlayerGoals}/${
                                                                      split.extraPlayerShots
                                                                  } (${percentText(
                                                                      split.extraPlayerGoals,
                                                                      split.extraPlayerShots
                                                                  )})`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            ) : null}

                            {quarterControl.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Quarter won → match result</h2>
                                        <span>
                                            When that quarter went our way vs{' '}
                                            {opponentName || 'them'}
                                        </span>
                                    </div>
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Period</th>
                                                    <th>Won</th>
                                                    <th>Drew</th>
                                                    <th>Lost</th>
                                                    <th>When won Q</th>
                                                    <th>When lost Q</th>
                                                    <th>When drew Q</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quarterControl.map((row) => (
                                                    <tr key={row.label}>
                                                        <td>{row.label}</td>
                                                        <td>
                                                            {row.won}/{row.played}
                                                        </td>
                                                        <td>{row.drawn}</td>
                                                        <td>{row.lost}</td>
                                                        <td>{wdlText(row.whenWon)}</td>
                                                        <td>{wdlText(row.whenLost)}</td>
                                                        <td>{wdlText(row.whenDrawn)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            ) : null}
                        </div>
                    ) : null}

                    {venue ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Home and away vs them</h2>
                                <span>Finished meetings</span>
                            </div>
                            <div className="insightsTableCluster insightsTableClusterInner">
                                <table className="insightsPlayerTable insightsTableFit">
                                    <thead>
                                        <tr>
                                            <th>Venue</th>
                                            <th>P</th>
                                            <th>W</th>
                                            <th>D</th>
                                            <th>L</th>
                                            <th>GF/g</th>
                                            <th>GA/g</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label: 'Home', summary: venue.home },
                                            { label: 'Away', summary: venue.away },
                                        ].map((row) => (
                                            <tr key={row.label}>
                                                <td>{row.label}</td>
                                                <td>{row.summary.played}</td>
                                                <td>{row.summary.wins}</td>
                                                <td>{row.summary.draws}</td>
                                                <td>{row.summary.losses}</td>
                                                <td>
                                                    {perGame(
                                                        row.summary.goalsFor,
                                                        row.summary.played
                                                    )}
                                                </td>
                                                <td>
                                                    {perGame(
                                                        row.summary.goalsAgainst,
                                                        row.summary.played
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {venueEvents.length > 0 ? (
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Home</th>
                                                <th>Away</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {venueEvents.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.left}</td>
                                                    <td>{row.right}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : null}
                            </div>
                        </section>
                    ) : null}

                    {whyWeWin.length > 0 || showGameState ? (
                        <div className="insightsTableCluster">
                            {whyWeWin.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Why we win vs them</h2>
                                        <span>Wins vs losses in these meetings</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Wins</th>
                                                <th>Losses</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {whyWeWin.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.wins}</td>
                                                    <td>{row.losses}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}

                            {showGameState ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Game state</h2>
                                        <span>
                                            Our shots by score · clutch = last 5′ and within 2
                                        </span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>State</th>
                                                <th>Goals</th>
                                                <th>Shots</th>
                                                <th>%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(
                                                [
                                                    ['Leading', gameState.leading],
                                                    ['Tied', gameState.tied],
                                                    ['Trailing', gameState.trailing],
                                                    ['Clutch', gameState.clutch],
                                                ] as const
                                            ).map(([label, bucket]) => (
                                                <tr key={label}>
                                                    <td>{label}</td>
                                                    <td>{bucket.goals}</td>
                                                    <td>{bucket.shots}</td>
                                                    <td>
                                                        {shootPct(bucket) == null
                                                            ? '-'
                                                            : `${shootPct(bucket)}%`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}
                        </div>
                    ) : null}

                    {usTally.sogFaced > 0 || (themTally && themTally.sogFaced > 0) ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Goalkeeping</h2>
                                <span>
                                    Us and {opponentName || 'them'} · SOG faced in these meetings
                                </span>
                            </div>
                            <table className="insightsPlayerTable insightsTableFit">
                                <thead>
                                    <tr>
                                        <th>Side</th>
                                        <th>Situation</th>
                                        <th>Saves</th>
                                        <th>GA</th>
                                        <th>Save %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usTally.sogFaced > 0 ? (
                                        <>
                                            <tr>
                                                <td>Us</td>
                                                <td>Overall</td>
                                                <td>{usTally.saves}</td>
                                                <td>{usTally.goalsAgainst}</td>
                                                <td>{pctText(savePct(usTally))}</td>
                                            </tr>
                                            {ATTACK_ORDER.map((type) => {
                                                const bucket = usTally.gkBySit[type];
                                                if (bucket.saves + bucket.goalsAgainst === 0) {
                                                    return null;
                                                }
                                                return (
                                                    <tr key={`us-${type}`}>
                                                        <td>Us</td>
                                                        <td>{ATTACK_LABELS[type]}</td>
                                                        <td>{bucket.saves}</td>
                                                        <td>{bucket.goalsAgainst}</td>
                                                        <td>{pctText(gkSitPct(bucket))}</td>
                                                    </tr>
                                                );
                                            })}
                                        </>
                                    ) : null}
                                    {themTally && themTally.sogFaced > 0
                                        ? [
                                              <tr key="them-overall">
                                                  <td>{opponentName || 'Them'}</td>
                                                  <td>Overall</td>
                                                  <td>{themTally.saves}</td>
                                                  <td>{themTally.goalsAgainst}</td>
                                                  <td>{pctText(savePct(themTally))}</td>
                                              </tr>,
                                              ...ATTACK_ORDER.map((type) => {
                                                  const bucket = themTally.gkBySit[type];
                                                  if (bucket.saves + bucket.goalsAgainst === 0) {
                                                      return null;
                                                  }
                                                  return (
                                                      <tr key={`them-${type}`}>
                                                          <td>{opponentName || 'Them'}</td>
                                                          <td>{ATTACK_LABELS[type]}</td>
                                                          <td>{bucket.saves}</td>
                                                          <td>{bucket.goalsAgainst}</td>
                                                          <td>{pctText(gkSitPct(bucket))}</td>
                                                      </tr>
                                                  );
                                              }),
                                          ]
                                        : null}
                                </tbody>
                            </table>
                        </section>
                    ) : null}

                    {usTally.matches > 0 ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Swim-offs, turnovers, extras</h2>
                                <span>Our side in these meetings</span>
                            </div>
                            <div className="insightsStatStrip">
                                <div className="insightsStat">
                                    <b>
                                        {usTally.swimoffs === 0
                                            ? '-'
                                            : `${usTally.swimoffWins}/${usTally.swimoffs}`}
                                    </b>
                                    <i>Swim-off W</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.turnovers}</b>
                                    <i>Turnovers</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.steals}</b>
                                    <i>Steals</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.blocks}</b>
                                    <i>Blocks</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.exclusionsDrawn - usTally.exclusionsCommitted}</b>
                                    <i>Ex net</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.timeouts}</b>
                                    <i>Timeouts</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{usTally.cards}</b>
                                    <i>Cards</i>
                                </div>
                            </div>
                            {turnoverRows.length > 0 ? (
                                <table className="insightsPlayerTable insightsTableFit">
                                    <thead>
                                        <tr>
                                            <th>Turnover type</th>
                                            <th>Count</th>
                                            <th>/ g</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {turnoverRows.map((row) => (
                                            <tr key={row.label}>
                                                <td>{row.label}</td>
                                                <td>{row.count}</td>
                                                <td>
                                                    {usTally.matches > 0
                                                        ? (row.count / usTally.matches).toFixed(1)
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : null}
                        </section>
                    ) : null}

                    {ourScorers.length > 0 ||
                    ourKeepers.length > 0 ||
                    ourSupport.length > 0 ||
                    ourExclusions.length > 0 ? (
                        <>
                            <div className="insightsProfileSectionHead">
                                <h2>Our players vs {opponentName || 'them'}</h2>
                                <span>Filtered meetings</span>
                            </div>
                            {ourScorers.length > 0 ? (
                                <TwScorersBoard
                                    rows={ourScorers}
                                    cell={rateCell}
                                    rateMode="totals"
                                    heading="Our scorers"
                                />
                            ) : null}
                            {ourKeepers.length > 0 ? (
                                <TwSavesBoard
                                    rows={ourKeepers}
                                    cell={rateCell}
                                    rateMode="totals"
                                    heading="Our saves"
                                />
                            ) : null}
                            {ourSupport.length > 0 ? (
                                <TwSupportBoard rows={ourSupport} cell={rateCell} />
                            ) : null}
                            {ourExclusions.length > 0 ? (
                                <TwExclusionsBoard rows={ourExclusions} cell={rateCell} />
                            ) : null}
                        </>
                    ) : null}

                    {showOppBoards ? (
                        <>
                            <div className="insightsProfileSectionHead">
                                <h2>Opponent · {opponentName}</h2>
                                <span>
                                    Their players in these meetings · only names logged in your
                                    fixtures
                                </span>
                            </div>
                            {oppScorers.length > 0 ? (
                                <TwScorersBoard
                                    rows={oppScorers}
                                    cell={rateCell}
                                    rateMode="totals"
                                    heading={`${opponentName} · Scorers`}
                                />
                            ) : null}
                            {oppKeepers.length > 0 ? (
                                <TwSavesBoard
                                    rows={oppKeepers}
                                    cell={rateCell}
                                    rateMode="totals"
                                    heading={`${opponentName} · Saves`}
                                />
                            ) : null}
                        </>
                    ) : null}

                    <section className="insightsPanel">
                        <div className="insightsPanelHead">
                            <h2>Previous meetings</h2>
                            <span>Newest first</span>
                        </div>
                        <InsightsMatchTable
                            matches={filteredMatches}
                            ourTeamIds={teamIds}
                            clubPerspective
                            emptyText="No meetings recorded."
                        />
                    </section>
                </Spin>
            </div>
        </div>
    );
};

export default InsightsPrep;
